import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  DocumentTextIcon,
  DocumentIcon,
  PhotoIcon,
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  XMarkIcon,
  FunnelIcon,
  CheckIcon,
  DocumentPlusIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  FolderIcon,
  CircleStackIcon,
  SparklesIcon,
  ArrowsUpDownIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import useDocumentManagement from '../hooks/useDocumentManagement';

import DocumentPagination from '../components/DocumentPagination';
import AttributeLabel from '../components/AttributeLabel';
import DocumentDetailModal from '../components/modals/DocumentDetailModal';
import DocumentAiModal from '../components/modals/DocumentAiModal';
import DocumentAccessModal from '../components/modals/DocumentAccessModal';
import QuickBookingModal from '../components/modals/QuickBookingModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { ModalShell, ModalActions } from '../components/modals/core';

/** Arbeitsstatus eines Belegs, abgeleitet aus den vorhandenen Feldern. */
type DocWorkStatus = 'neu' | 'pruefen' | 'analysiert';

type DocSort = 'neu' | 'alt' | 'name' | 'betrag' | 'status' | 'belegnr';

/** Darstellung der Status-Badges. Text trägt die Information, Farbe unterstützt. */
const STATUS_BADGE: Record<DocWorkStatus, { label: string; cls: string }> = {
  analysiert: {
    label: 'Verbucht',
    cls: 'bg-success-600 text-white border-success-600',
  },
  pruefen: {
    label: 'KI-Vorschlag prüfen',
    cls: 'bg-warning-50 text-warning-700 border-warning-500 dark:bg-warning-900 dark:text-warning-100',
  },
  neu: {
    label: 'Neu',
    cls: 'bg-gray-200 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600',
  },
};

const DocumentManagement: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultsStartRef = useRef<HTMLDivElement>(null);
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  /** Zähler: Detail-Modal soll nachladen, weil das KI-Modal etwas geändert hat. */
  const [detailRefresh, setDetailRefresh] = useState(0);
  /**
   * Thumbnail-Version je Dokument. Nach einer Drehung soll nur DIESES Thumbnail
   * neu laden — ein globaler Versionswechsel lud alle ~280 Vorschaubilder auf
   * einmal neu, blockierte den Browser und verzögerte die Vorschau im Modal.
   */
  const [docThumbVersion, setDocThumbVersion] = useState<Record<number, number>>({});
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [docsToDelete, setDocsToDelete] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageLoadingStates, setImageLoadingStates] = useState<Set<number>>(new Set());
  const [imageErrorStates, setImageErrorStates] = useState<Set<number>>(new Set());
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [quickBookingDoc, setQuickBookingDoc] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant?: 'single' | 'two';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });
  const [thumbnailVersion, setThumbnailVersion] = useState<number>(Date.now());
  const [filterCreator, setFilterCreator] = useState<string>('all');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [showConsumptionDocuments, setShowConsumptionDocuments] = useState(false);
  const [showAccountStatements, setShowAccountStatements] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  // Status-Filter und Sortierung aus dem Design-Entwurf.
  const [statusFilter, setStatusFilter] = useState<'alle' | 'pruefen' | 'ohne' | 'analysiert'>('alle');
  const [sortBy, setSortBy] = useState<DocSort>('neu');
  const [aiModalDoc, setAiModalDoc] = useState<any>(null);
  const [documentsPerPage, setDocumentsPerPage] = useState(24);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepth = useRef(0);

  const {
    documents,
    isLoadingDocuments,
    loadError,
    filteredDocs,
    searchTerm,
    setSearchTerm,
    selectedDocs,
    setSelectedDocs,
    pdfError,
    filterType,
    setFilterType,
    handleFileUpload,
    handleDeleteDocument,
    handleDeleteMultiple,
    handleMergeDocuments,
    downloadDocument,
    formatSize,
    fetchDocuments,
  } = useDocumentManagement(user?.mg_nr || 'unknown');

  // Unique creators + counts
  const uniqueCreators = Array.from(new Set(documents.map(doc => doc.member_name).filter(Boolean)));
  const getCreatorCount = (name: string) => documents.filter(d => d.member_name === name).length;

  const isConsumptionDocument = (doc: any) => (doc.linked_drinks_events?.length || 0) > 0;
  const isAccountStatement = (doc: any) => Number(doc.is_bank_statement) === 1;
  const consumptionDocumentCount = documents.filter(isConsumptionDocument).length;
  const accountStatementCount = documents.filter(isAccountStatement).length;

  // Additional filtering
  const additionalFilteredDocs = filteredDocs.filter(doc => {
    // Fachansichten verwalten diese Dokumente bereits. In der allgemeinen
    // Dokumentenablage erscheinen sie nur, wenn sie bewusst eingeblendet werden.
    if (!showConsumptionDocuments && isConsumptionDocument(doc)) return false;
    if (!showAccountStatements && isAccountStatement(doc)) return false;
    if (filterCreator !== 'all' && doc.member_name !== filterCreator) return false;
    if (filterDateFrom) {
      if (new Date(doc.created_at) < new Date(`${filterDateFrom}T00:00:00`)) return false;
    }
    if (filterDateTo) {
      const toDate = new Date(`${filterDateTo}T00:00:00`);
      toDate.setHours(23, 59, 59, 999);
      if (new Date(doc.created_at) > toDate) return false;
    }
    return true;
  });

  /* ------------------------------------------------------------------ *
   * Arbeitsstatus je Dokument (Design-Entwurf „Dokumente Klickdummy")
   *
   * Der Status wird aus vorhandenen Feldern abgeleitet — es gibt dafür keine
   * Spalte in der Datenbank:
   *   neu        keine KI-Daten vorhanden
   *   pruefen    KI-Werte da, aber noch keine Buchung verknüpft
   *   analysiert KI-Werte da und verbucht
   * ------------------------------------------------------------------ */
  const hasBooking = (doc: any) =>
    doc.is_linked === 1 || (doc.linked_bookings && doc.linked_bookings.length > 0);

  const docStatus = (doc: any): DocWorkStatus => {
    const hasAi = !!doc.ai_comment || !!doc.invoice_amount || !!doc.invoice_date;
    if (hasBooking(doc)) return 'analysiert';
    return hasAi ? 'pruefen' : 'neu';
  };

  const statusCounts = {
    alle: additionalFilteredDocs.length,
    pruefen: additionalFilteredDocs.filter(d => docStatus(d) === 'pruefen').length,
    ohne: additionalFilteredDocs.filter(d => !hasBooking(d)).length,
    analysiert: additionalFilteredDocs.filter(d => docStatus(d) === 'analysiert').length,
  };

  /** Betrag als Zahl; fehlt er, kommt null zurück. */
  const amountOf = (doc: any): number | null => {
    const n = parseFloat(String(doc.invoice_amount ?? '').replace(',', '.'));
    return Number.isFinite(n) && n !== 0 ? n : null;
  };

  // Status-Filter des Entwurfs, zusätzlich zu Typ/Uploader/Zeitraum
  const statusFilteredDocs = additionalFilteredDocs.filter(doc => {
    if (statusFilter === 'alle') return true;
    if (statusFilter === 'ohne') return !hasBooking(doc);
    return docStatus(doc) === statusFilter;
  });

  // Sortierung
  const STATUS_RANK: Record<DocWorkStatus, number> = { pruefen: 0, neu: 1, analysiert: 2 };
  const time = (d: any) => new Date(d.created_at).getTime() || 0;
  const sortedDocs = [...statusFilteredDocs].sort((a, b) => {
    switch (sortBy) {
      case 'alt':
        return time(a) - time(b);
      case 'name':
        return String(a.name).localeCompare(String(b.name), 'de');
      case 'betrag':
        return Math.abs(amountOf(b) ?? 0) - Math.abs(amountOf(a) ?? 0);
      case 'belegnr': {
        // Dokumente ohne Nummer ans Ende; numerisch ("2" vor "10").
        const an = a.beleg_nr ? String(a.beleg_nr) : null;
        const bn = b.beleg_nr ? String(b.beleg_nr) : null;
        if (an === null && bn === null) return time(b) - time(a);
        if (an === null) return 1;
        if (bn === null) return -1;
        return an.localeCompare(bn, 'de', { numeric: true });
      }
      case 'status':
        return STATUS_RANK[docStatus(a)] - STATUS_RANK[docStatus(b)] || time(b) - time(a);
      default:
        return time(b) - time(a);
    }
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedDocs.length / documentsPerPage));
  const visiblePage = Math.min(currentPage, totalPages);
  const startIndex = (visiblePage - 1) * documentsPerPage;
  const paginatedDocs = sortedDocs.slice(startIndex, startIndex + documentsPerPage);

  const changePage = (page: number) => {
    if (page === visiblePage) return;
    setCurrentPage(page);
    // Pagination alone scrolls; filters and field editing keep their focus and position.
    requestAnimationFrame(() => resultsStartRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' }));
  };

  // Stats
  const pdfCount = documents.filter(d => /\.pdf$/i.test(d.file_path)).length;
  const imageCount = documents.filter(d => /\.(jpg|jpeg|png|gif|webp)$/i.test(d.file_path)).length;
  const totalSize = documents.reduce((sum, d) => sum + (d.size || 0), 0);

  // Active filter count
  const activeFiltersCount =
    (filterType !== 'all' ? 1 : 0) +
    (filterCreator !== 'all' ? 1 : 0) +
    (filterDateFrom ? 1 : 0) +
    (filterDateTo ? 1 : 0) +
    (showConsumptionDocuments ? 1 : 0) +
    (showAccountStatements ? 1 : 0);

  const hasActiveFilters = activeFiltersCount > 0 || Boolean(searchTerm) || statusFilter !== 'alle';

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('alle');
    setFilterType('all');
    setFilterCreator('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setShowConsumptionDocuments(false);
    setShowAccountStatements(false);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, filterCreator, filterDateFrom, filterDateTo, showConsumptionDocuments, showAccountStatements, statusFilter, documentsPerPage]);

  useEffect(() => {
    setCurrentPage(page => Math.min(page, totalPages));
  }, [totalPages]);

  const uploadFiles = async (files: FileList) => {
    if (!files.length || isUploading) return;
    setIsUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    try {
      const result = await handleFileUpload(files);
      const failed = result.errors.length;
      setConfirmModal({
        isOpen: true,
        type: failed ? (result.uploaded.length ? 'warning' : 'error') : 'success',
        title: failed ? 'Upload prüfen' : 'Upload erfolgreich',
        message: `${result.uploaded.length} Dokument(e) hochgeladen.${failed ? ` ${failed} Datei(en) fehlgeschlagen: ${result.errors.map(item => `${item.file}: ${item.error}`).join('; ')}` : ''}`,
        variant: 'single',
      });
      if (result.uploaded.length) setCurrentPage(1);
    } catch (error: any) {
      setConfirmModal({ isOpen: true, type: 'error', title: 'Upload fehlgeschlagen', message: error.response?.data?.error || error.message || 'Dokumente konnten nicht hochgeladen werden.', variant: 'single' });
    } finally {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) void uploadFiles(e.target.files);
  };

  const toggleDocSelection = (docId: number) => {
    setSelectedDocs(prev => prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]);
  };

  const handleMerge = async () => {
    if (selectedDocs.length < 2 || isMerging) return;
    setIsMerging(true);
    try {
      const newDocId = await handleMergeDocuments(selectedDocs);
      if (newDocId) {
        setIsMergeMode(false);
        setSelectedDocs([]);
      }
    } finally {
      setIsMerging(false);
    }
  };

  const confirmDelete = async () => {
    if (!docsToDelete.length || isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await handleDeleteMultiple(docsToDelete);
      setDocsToDelete([]);
      setShowDeleteConfirm(false);
      if (result.failedIds.length) {
        setConfirmModal({ isOpen: true, type: 'warning', title: 'Löschen teilweise fehlgeschlagen', message: `${result.deletedIds.length} Dokument(e) gelöscht. ${result.failedIds.length} konnten nicht gelöscht werden und bleiben ausgewählt.`, variant: 'single' });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    setImageErrorStates(new Set());
  }, [thumbnailVersion, docThumbVersion]);

  const handleImageLoad = (docId: number) => {
    setImageLoadingStates(prev => { const s = new Set(prev); s.delete(docId); return s; });
    setImageErrorStates(prev => { const s = new Set(prev); s.delete(docId); return s; });
  };

  const handleImageError = (docId: number) => {
    setImageLoadingStates(prev => { const s = new Set(prev); s.delete(docId); return s; });
    setImageErrorStates(prev => new Set(prev).add(docId));
  };

  const getSecureImageUrl = (docId: number) => {
    const token = localStorage.getItem('jwt_token');
    const baseUrl = (BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/$/, '');
    return `${baseUrl}/api/documents/download-url/${docId}?thumbnail=true&token=${encodeURIComponent(token || '')}&v=${docThumbVersion[docId] ?? thumbnailVersion}`;
  };

  const getDocumentPreview = (doc: any, heightClass = 'h-32') => {
    const ext = (doc.file_path || '').split('.').pop()?.toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '');
    const isPdf = ext === 'pdf';
    const hasImageError = imageErrorStates.has(doc.id);
    const isLoadingImage = imageLoadingStates.has(doc.id);
    const hasLoadedImage = !isLoadingImage && !hasImageError;

    if (isImage || isPdf) {
      return (
        <div className={`relative w-full ${heightClass} bg-gray-100 dark:bg-gray-800 overflow-hidden`}>
          {(hasImageError || (!hasLoadedImage && !isLoadingImage)) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <DocumentIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
              <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 uppercase tracking-wide">{isPdf ? 'PDF' : ext}</span>
            </div>
          )}
          {isLoadingImage && (
            <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 animate-pulse flex items-center justify-center">
              <DocumentIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
            </div>
          )}
          {!hasImageError && (
            <img
              src={getSecureImageUrl(doc.id)}
              alt={doc.name}
              className={`w-full ${heightClass} object-cover transition-opacity ${hasLoadedImage && !isLoadingImage ? 'opacity-100' : 'opacity-0'}`}
              onLoadStart={() => setImageLoadingStates(prev => new Set(prev).add(doc.id))}
              onLoad={() => handleImageLoad(doc.id)}
              onError={() => handleImageError(doc.id)}
              loading="lazy"
            />
          )}
        </div>
      );
    }

    return (
      <div className={`w-full ${heightClass} flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800`}>
        <DocumentTextIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
        <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 uppercase tracking-wide">Dokument</span>
      </div>
    );
  };

  // ─── Filter Sidebar Content (shared between desktop sidebar and mobile sheet) ───
  const renderFilterContent = () => (
    <>
      {/* Search */}
      <div className="relative mb-4">
        <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Name, Belegnummer oder Inhalt suchen …" aria-label="Dokumente suchen"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="min-h-[40px] w-full pl-8 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Dateityp */}
      <div className="mb-4">
        <p className="mb-2">
          <AttributeLabel iconKey="documentType" size="sm" textClassName="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Dateityp</AttributeLabel>
        </p>
        <div className="space-y-1.5">
          <label className="flex min-h-[40px] items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
            <input type="checkbox" checked={filterType === 'all'} onChange={() => setFilterType('all')} className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500" />
            Alle ({documents.length})
          </label>
          <label className="flex min-h-[40px] items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
            <input type="checkbox" checked={filterType === 'pdf'} onChange={() => setFilterType(filterType === 'pdf' ? 'all' : 'pdf')} className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500" />
            PDF ({pdfCount})
          </label>
          <label className="flex min-h-[40px] items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
            <input type="checkbox" checked={filterType === 'image'} onChange={() => setFilterType(filterType === 'image' ? 'all' : 'image')} className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500" />
            Bilder ({imageCount})
          </label>
        </div>
      </div>

      {/* Fachlich zugeordnete Dokumente */}
      <div className="mb-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Zusätzlich einblenden
        </p>
        <div className="space-y-1.5">
          <label className="flex min-h-[40px] cursor-pointer items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={showConsumptionDocuments}
              onChange={(e) => setShowConsumptionDocuments(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
            />
            Verzehrkosten ({consumptionDocumentCount})
          </label>
          <label className="flex min-h-[40px] cursor-pointer items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={showAccountStatements}
              onChange={(e) => setShowAccountStatements(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
            />
            Kontoauszüge ({accountStatementCount})
          </label>
        </div>
        <p className="mt-2 text-[11px] leading-4 text-gray-400 dark:text-gray-500">
          Diese Dokumente sind standardmäßig ausgeblendet.
        </p>
      </div>

      {/* Zeitraum */}
      <div className="mb-4">
        <p className="mb-2">
          <AttributeLabel iconKey="date" size="sm" textClassName="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Zeitraum</AttributeLabel>
        </p>
        <div className="space-y-1.5">
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="min-h-[40px] w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500"
            placeholder="Von" aria-label="Upload-Datum von"
          />
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="min-h-[40px] w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500"
            placeholder="Bis" aria-label="Upload-Datum bis"
          />
        </div>
      </div>

      {/* Uploader */}
      {uniqueCreators.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Uploader</p>
          <div className="space-y-1.5">
            {uniqueCreators.map(creator => (
              <label key={creator} className="flex min-h-[40px] items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterCreator === creator}
                  onChange={() => setFilterCreator(filterCreator === creator ? 'all' : (creator || 'all'))}
                  className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                />
                {creator} ({getCreatorCount(creator || '')})
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Reset */}
      {hasActiveFilters && (
        <button
          onClick={clearAllFilters}
          className="w-full py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Filter zurücksetzen
        </button>
      )}
    </>
  );

  // ─── Document Card (render function, NOT component – avoids remount on re-render) ───
  // Aufbau nach dem Design-Entwurf: Statusbadge über der Vorschau, Betrag in
  // Tabellenziffern, Fortschritt während der Analyse, Buchungshinweis in der Fußzeile.
  const renderDocumentCard = (doc: any) => {
    const status = docStatus(doc);
    const badge = STATUS_BADGE[status];
    const amount = amountOf(doc);
    const bookingCount = doc.linked_bookings?.length || 0;
    const isSelected = selectedDocs.includes(doc.id);

    const footerText =
      status === 'neu'
        ? 'Noch nicht geprüft'
        : bookingCount === 0
        ? 'Ohne Buchung'
        : `${bookingCount} ${bookingCount === 1 ? 'Buchung' : 'Buchungen'}`;

    const footerColor =
      status === 'pruefen' || (status !== 'neu' && bookingCount === 0)
        ? 'text-warning-700 dark:text-warning-400'
        : status === 'neu'
        ? 'text-gray-500 dark:text-gray-400'
        : 'text-success-700 dark:text-success-400';

    return (
      <div
        key={doc.id}
        className={`relative flex cursor-pointer flex-col overflow-hidden rounded-xl border bg-white transition-all duration-200 dark:bg-gray-800 ${
          isSelected
            ? 'border-primary-500 ring-2 ring-primary-500'
            : status === 'pruefen'
            ? 'border-warning-300 hover:shadow-md dark:border-warning-800/70'
            : 'border-gray-200 hover:shadow-md dark:border-gray-700'
        }`}
        role="group"
        aria-label={`Dokument: ${doc.name}`}
        onClick={() => (isMergeMode ? toggleDocSelection(doc.id) : setSelectedDocId(doc.id))}
      >
        {/* Auswahl im Merge-Modus */}
        {isMergeMode && (
          <div className="absolute left-2 top-2 z-20">
            <div
              className={`flex h-5 w-5 items-center justify-center rounded ${
                isSelected
                  ? 'bg-primary-600'
                  : 'border-2 border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-700'
              }`}
            >
              {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
            </div>
          </div>
        )}

        {/* Vorschau mit Statusbadge und Seitenangabe */}
        <div className="relative">
          {getDocumentPreview(doc, 'h-32')}
          {!isMergeMode && (
            <span
              className={`absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${badge.cls}`}
            >
              {badge.label}
            </span>
          )}
          {doc.linked_drinks_events && doc.linked_drinks_events.length > 0 && (
            <span
              className="absolute right-2 top-2 z-10 rounded border border-primary-400 bg-white/90 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:border-primary-500 dark:bg-gray-800/90 dark:text-primary-300"
              title={`Getränke: ${doc.linked_drinks_events.map((e: any) => e.beschriftung).join(', ')}`}
            >
              Getränke
            </span>
          )}
          <span className="absolute bottom-1.5 right-1.5 z-10 rounded bg-black/10 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700 dark:bg-black/40 dark:text-gray-200">
            {(doc.file_path || '').split('.').pop()?.toUpperCase() || 'DATEI'}
          </span>
        </div>

        {/* Angaben */}
        <div className="flex flex-1 flex-col gap-2 p-3">
          <h3 className="min-w-0 text-[13px] font-bold text-gray-900 dark:text-white">
            <button
              type="button"
              className="block min-h-[40px] w-full truncate text-left hover:text-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-primary-300"
              title={doc.name}
              aria-label={`${isMergeMode ? 'Auswählen' : 'Öffnen'}: ${doc.name}`}
              aria-pressed={isMergeMode ? isSelected : undefined}
              onClick={event => {
                event.stopPropagation();
                isMergeMode ? toggleDocSelection(doc.id) : setSelectedDocId(doc.id);
              }}
            >
              {doc.name}
            </button>
          </h3>

          {doc.beleg_nr && (
            <span
              className="inline-flex w-fit items-center rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
              title="Belegnummer"
            >
              Beleg-Nr. {doc.beleg_nr}
            </span>
          )}

          {amount !== null && (
            <span className="text-sm font-bold tabular-nums text-gray-900 dark:text-white">
              {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)}
            </span>
          )}

          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {new Date(doc.created_at).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}{' '}
            · {formatSize(doc.size)}
            {doc.member_name ? ` · ${doc.member_name}` : ''}
          </p>

          {!isMergeMode && (
            <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2 dark:border-gray-700">
              <span className={`text-[11px] font-semibold ${footerColor}`}>{footerText}</span>

              <span className="ml-auto flex items-center gap-1">
                {(status === 'neu' || status === 'pruefen') && (
                  // Ein Weg für beides: das KI-Modal liest den Beleg selbst,
                  // wenn noch keine Analyse vorliegt. Offene Vorschläge sind
                  // gefüllt, damit sie in der Liste auffallen.
                  <button
                    onClick={e => { e.stopPropagation(); setAiModalDoc(doc); }}
                    className={`inline-flex items-center gap-1 min-h-[40px] rounded-lg px-2 py-1 text-[11px] font-bold ${
                      status === 'pruefen'
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
                    }`}
                    title="Beleg prüfen"
                  >
                    <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Prüfen
                  </button>
                )}
                {!hasBooking(doc) && (
                  <button
                    onClick={e => { e.stopPropagation(); setQuickBookingDoc(doc); }}
                    className="min-h-[40px] rounded-lg border border-gray-200 px-2 py-1 text-[11px] font-bold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                    title="Buchung anlegen"
                  >
                    Buchen
                  </button>
                )}
                <button
                  onClick={e => { e.stopPropagation(); downloadDocument(doc.id, doc.name); }}
                  className="inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-primary-600 dark:hover:bg-gray-700"
                  title="Herunterladen" aria-label={`Herunterladen: ${doc.name}`}
                >
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  /** Kompakte Upload-Zone, ohne einen Dokumentplatz im Raster zu belegen. */
  const renderUploadTile = () => (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={isUploading}
      className="mb-2 flex min-h-[48px] w-full items-center justify-center gap-3 rounded-xl border border-dashed border-gray-300 bg-white px-3 py-2 text-left transition-colors hover:border-primary-500 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-primary-400 dark:hover:bg-gray-700"
    >
      <CloudArrowUpIcon className="h-6 w-6 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
      <span>
        <span className="block text-sm font-semibold text-gray-700 dark:text-gray-200">Dateien hierher ziehen oder auswählen</span>
        <span className="block text-xs text-gray-500 dark:text-gray-400">PDF oder Bild · KI-Auswertung über „Prüfen“</span>
      </span>
    </button>
  );

  // ─── Active Filter Chips (render function) ───
  const renderActiveFilterChips = () => {
    if (!hasActiveFilters) return null;
    return (
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs text-gray-500 dark:text-gray-400">Aktive Filter:</span>
        {filterType !== 'all' && (
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 px-2 py-0.5 rounded-full flex items-center gap-1">
            {filterType === 'pdf' ? 'PDF' : 'Bilder'}
            <button onClick={() => setFilterType('all')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">&times;</button>
          </span>
        )}
        {filterCreator !== 'all' && (
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 px-2 py-0.5 rounded-full flex items-center gap-1">
            {filterCreator}
            <button onClick={() => setFilterCreator('all')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">&times;</button>
          </span>
        )}
        {filterDateFrom && (
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 px-2 py-0.5 rounded-full flex items-center gap-1">
            Ab {new Date(filterDateFrom).toLocaleDateString('de-DE')}
            <button onClick={() => setFilterDateFrom('')} className="text-gray-400 hover:text-gray-600">&times;</button>
          </span>
        )}
        {filterDateTo && (
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 px-2 py-0.5 rounded-full flex items-center gap-1">
            Bis {new Date(filterDateTo).toLocaleDateString('de-DE')}
            <button onClick={() => setFilterDateTo('')} className="text-gray-400 hover:text-gray-600">&times;</button>
          </span>
        )}
        {showConsumptionDocuments && (
          <span className="flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300">
            Verzehrkosten eingeblendet
            <button onClick={() => setShowConsumptionDocuments(false)} className="text-primary-400 hover:text-primary-700 dark:hover:text-primary-200">&times;</button>
          </span>
        )}
        {showAccountStatements && (
          <span className="flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300">
            Kontoauszüge eingeblendet
            <button onClick={() => setShowAccountStatements(false)} className="text-primary-400 hover:text-primary-700 dark:hover:text-primary-200">&times;</button>
          </span>
        )}
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">{additionalFilteredDocs.length} Ergebnisse</span>
      </div>
    );
  };

  return (
    <div
      className="max-w-[1600px] mx-auto [color-scheme:light] dark:[color-scheme:dark]"
      onDragEnter={event => {
        if (!event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        dragDepth.current += 1;
        setIsDragOver(true);
      }}
      onDragOver={event => {
        if (event.dataTransfer.types.includes('Files')) {
          event.preventDefault();
          event.dataTransfer.dropEffect = isUploading ? 'none' : 'copy';
        }
      }}
      onDragLeave={event => {
        event.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (!dragDepth.current) setIsDragOver(false);
      }}
      onDrop={event => {
        event.preventDefault();
        dragDepth.current = 0;
        setIsDragOver(false);
        void uploadFiles(event.dataTransfer.files);
      }}
    >
      {isDragOver && !isUploading && (
        <div className="pointer-events-none fixed inset-4 z-[100] flex items-center justify-center rounded-2xl border-2 border-dashed border-primary-500 bg-primary-50/95 p-6 text-center text-xl font-bold text-primary-700 dark:border-primary-400 dark:bg-gray-900/95 dark:text-primary-300" role="status">
          Dateien loslassen zum Hochladen
        </div>
      )}
      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" />

      {/* ═══════════════════ MAIN LAYOUT ═══════════════════ */}
      <div className="pb-8">
        <div className="mx-auto w-full max-w-[1600px]">
          {/* Desktop & Tablet Header */}
          <header className="app-page-header hidden sm:flex items-center justify-between bg-white px-4 py-3 dark:bg-gray-800 mb-2">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
                <FolderIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Belege &amp; Dokumente</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedDocs.length > 0 && (
                <>
                  <button aria-label="Ausgewählte Dokumente zusammenführen" onClick={handleMerge} disabled={selectedDocs.length < 2 || isMerging || isDeleting} className="px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:bg-gray-400 flex items-center gap-1.5">
                    <DocumentPlusIcon className="h-4 w-4" /> Merge ({selectedDocs.length})
                  </button>
                  <button aria-label="Ausgewählte Dokumente löschen" onClick={() => { setDocsToDelete(selectedDocs); setShowDeleteConfirm(true); }} className="min-h-[40px] min-w-[40px] p-2 bg-error-500 text-white rounded-lg hover:bg-error-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </>
              )}
              <button
                aria-label={isMergeMode ? 'Auswahl beenden' : 'Dokumente auswählen'}
                onClick={() => { setIsMergeMode(!isMergeMode); if (isMergeMode) setSelectedDocs([]); }}
                className={`min-h-[40px] min-w-[40px] p-2 rounded-lg transition-colors ${isMergeMode ? 'bg-primary-600 text-white' : 'border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                title={isMergeMode ? 'Auswahl beenden' : 'Auswählen'}
              >
                {isMergeMode ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentListIcon className="h-4 w-4" />}
              </button>
              {user?.vorstand === 1 && (
                <button onClick={() => setShowAccessModal(true)} aria-label="Dokumentenrechte verwalten" className="hidden lg:flex min-h-[40px] min-w-[40px] items-center justify-center p-2 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800" title="Dokumentenrechte">
                  <ShieldCheckIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => {
                  const next = sortedDocs.find(d => docStatus(d) === 'pruefen')
                    || documents.find(d => docStatus(d) === 'pruefen');
                  if (next) {
                    setAiModalDoc(next);
                  } else {
                    setConfirmModal({
                      isOpen: true,
                      type: 'info',
                      title: 'Keine offenen Vorschläge',
                      message: 'Alle analysierten Belege sind verbucht.',
                      variant: 'single',
                    });
                  }
                }}
                className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                title="Offene KI-Vorschläge prüfen"
              >
                <SparklesIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                {statusCounts.pruefen > 0 ? `${statusCounts.pruefen} Vorschläge prüfen` : 'Alle verbucht'}
              </button>
              <button onClick={() => fileInputRef.current?.click()} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 flex items-center gap-2">
                <CloudArrowUpIcon className="w-4 h-4" /> Upload
              </button>
            </div>
          </header>

          {/* Mobile Header */}
          <div className="flex sm:hidden items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
                <FolderIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              </div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Dokumente</h1>
            </div>
            <div className="flex gap-1.5">
              {selectedDocs.length > 0 && (
                <>
                  <button aria-label="Ausgewählte Dokumente zusammenführen" onClick={handleMerge} disabled={selectedDocs.length < 2 || isMerging || isDeleting} className="min-h-[40px] min-w-[40px] p-2 bg-primary-600 text-white rounded-lg disabled:bg-gray-400">
                    <DocumentPlusIcon className="w-4 h-4" />
                  </button>
                  <button aria-label="Ausgewählte Dokumente löschen" onClick={() => { setDocsToDelete(selectedDocs); setShowDeleteConfirm(true); }} className="min-h-[40px] min-w-[40px] p-2 bg-error-500 text-white rounded-lg">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </>
              )}
              <button
                aria-label={isMergeMode ? 'Auswahl beenden' : 'Dokumente auswählen'}
                onClick={() => { setIsMergeMode(!isMergeMode); if (isMergeMode) setSelectedDocs([]); }}
                className={`min-h-[40px] min-w-[40px] p-2 rounded-lg ${isMergeMode ? 'bg-primary-600 text-white' : 'border border-gray-200 dark:border-gray-700 text-gray-400'}`}
              >
                {isMergeMode ? <CheckIcon className="w-4 h-4" /> : <ClipboardDocumentListIcon className="w-4 h-4" />}
              </button>
              <button aria-label="Filter öffnen" onClick={() => setShowMobileFilters(true)} className="relative min-h-[40px] min-w-[40px] p-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-400">
                <FunnelIcon className="w-4 h-4" />
                {activeFiltersCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{activeFiltersCount}</span>}
              </button>
              <button aria-label="Dokumente hochladen" onClick={() => fileInputRef.current?.click()} className="min-h-[40px] min-w-[40px] p-2 text-white bg-primary-600 rounded-lg">
                <CloudArrowUpIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Accent Line */}
          <div className="mb-2 mt-2 h-px bg-gray-200 dark:bg-gray-700 sm:hidden"></div>

          {/* Mobile Search */}
          {documents.length > 0 && (
            <div className="sm:hidden relative mb-3">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Name, Belegnummer oder Inhalt suchen …" aria-label="Dokumente suchen"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-primary-500"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          {/* Mobile Active Filter Chips */}
          <div className="sm:hidden">
            {(filterType !== 'all' || filterCreator !== 'all' || showConsumptionDocuments || showAccountStatements) && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {filterType !== 'all' && (
                  <span className="text-[10px] font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                    {filterType === 'pdf' ? 'PDF' : 'Bilder'} <button onClick={() => setFilterType('all')}>&times;</button>
                  </span>
                )}
                {showConsumptionDocuments && (
                  <span className="flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300">
                    Verzehrkosten <button onClick={() => setShowConsumptionDocuments(false)}>&times;</button>
                  </span>
                )}
                {showAccountStatements && (
                  <span className="flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300">
                    Kontoauszüge <button onClick={() => setShowAccountStatements(false)}>&times;</button>
                  </span>
                )}
                <span className="text-[10px] text-gray-500 dark:text-gray-400 ml-auto self-center">{sortedDocs.length} Treffer</span>
              </div>
            )}
          </div>

          {/* Mobile Quick Filter Chips */}
          {documents.length > 0 && (
            <div className="sm:hidden flex gap-1.5 overflow-x-auto pb-2 mb-3" style={{ WebkitOverflowScrolling: 'touch' }}>
              <button
                onClick={() => setFilterType(filterType === 'pdf' ? 'all' : 'pdf')}
                className={`min-h-[40px] flex-shrink-0 text-xs font-medium border px-2.5 py-1 rounded-full ${filterType === 'pdf' ? 'text-primary-700 bg-primary-50 border-primary-200 dark:text-primary-300 dark:bg-primary-900/25 dark:border-primary-800' : 'text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
              >
                PDF
              </button>
              <button
                onClick={() => setFilterType(filterType === 'image' ? 'all' : 'image')}
                className={`min-h-[40px] flex-shrink-0 text-xs font-medium border px-2.5 py-1 rounded-full ${filterType === 'image' ? 'text-primary-700 bg-primary-50 border-primary-200 dark:text-primary-300 dark:bg-primary-900/25 dark:border-primary-800' : 'text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
              >
                Bilder
              </button>
              <button
                onClick={() => setShowConsumptionDocuments(!showConsumptionDocuments)}
                aria-pressed={showConsumptionDocuments}
                className={`min-h-[40px] flex-shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium ${showConsumptionDocuments ? 'border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300' : 'border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'}`}
              >
                Verzehrkosten
              </button>
              <button
                onClick={() => setShowAccountStatements(!showAccountStatements)}
                aria-pressed={showAccountStatements}
                className={`min-h-[40px] flex-shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium ${showAccountStatements ? 'border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300' : 'border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'}`}
              >
                Kontoauszüge
              </button>
            </div>
          )}

          {/* ═══════════════════ CONTENT LAYOUT (Filter horizontal über den Cards) ═══════════════════ */}
          <div>
            <div className="min-w-0">
              {/* Stats */}
              {documents.length > 0 && (
                <dl className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs dark:border-gray-700 dark:bg-gray-800">
                  {([
                    { label: 'Dokumente', value: documents.length, icon: FolderIcon },
                    { label: 'PDFs', value: pdfCount, icon: DocumentTextIcon },
                    { label: 'Bilder', value: imageCount, icon: PhotoIcon },
                    { label: 'Speicher', value: formatSize(totalSize), icon: CircleStackIcon },
                  ]).map(stat => (
                    <div key={stat.label} className="inline-flex items-center gap-1.5">
                      <stat.icon aria-hidden="true" className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                      <dt className="text-gray-500 dark:text-gray-400">{stat.label}</dt>
                      <dd className="font-bold tabular-nums text-gray-900 dark:text-gray-100">{stat.value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {/* ── Status-Chips + Sortierung (Design-Entwurf) ── */}
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {([
                  { key: 'alle', label: 'Alle' },
                  { key: 'pruefen', label: 'KI prüfen' },
                  { key: 'ohne', label: 'Ohne Buchung' },
                  { key: 'analysiert', label: 'Verbucht' },
                ] as const).map(chip => {
                  const on = statusFilter === chip.key;
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => { setStatusFilter(chip.key); setCurrentPage(1); }}
                      aria-pressed={on}
                      className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
                        on
                          ? 'border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {chip.label}
                      <span className="text-[11px] tabular-nums opacity-75">{statusCounts[chip.key]}</span>
                    </button>
                  );
                })}

                <div className="flex w-full min-w-0 items-center gap-2 sm:ml-auto sm:w-auto">
                  <label
                    htmlFor="doc-sort"
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    <ArrowsUpDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Sortieren
                  </label>
                  <select
                    id="doc-sort"
                    value={sortBy}
                    onChange={e => { setSortBy(e.target.value as DocSort); setCurrentPage(1); }}
                    className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-2 text-[13px] font-semibold text-gray-900 focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white sm:flex-none"
                  >
                    <option value="neu">Neueste zuerst</option>
                    <option value="alt">Älteste zuerst</option>
                    <option value="name">Name A–Z</option>
                    <option value="betrag">Betrag absteigend</option>
                    <option value="belegnr">Belegnummer aufsteigend</option>
                    <option value="status">Status · KI-Vorschläge zuerst</option>
                  </select>
                </div>
              </div>

              {/* ── Desktop Filterleiste (horizontal über den Dokument-Cards) ── */}
              {documents.length > 0 && (
                <div className="hidden lg:block border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-[#1c2432] p-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Suche */}
                    <div className="relative w-60">
                      <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Name, Belegnummer oder Inhalt suchen …" aria-label="Dokumente suchen"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      />
                      {searchTerm && (
                        <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Dateityp - Segmented Control */}
                    <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      {([['all', `Alle (${documents.length})`], ['pdf', `PDF (${pdfCount})`], ['image', `Bilder (${imageCount})`]] as const).map(([value, label]) => (
                        <button
                          key={value}
                          onClick={() => setFilterType(value)}
                          className={`min-h-[40px] px-3 py-2 text-sm font-medium transition-colors ${
                            filterType === value
                              ? 'bg-primary-600 text-white'
                              : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {/* Fachlich zugeordnete Dokumente sind standardmäßig verborgen. */}
                    <label className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${showConsumptionDocuments ? 'border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800'}`}>
                      <input
                        type="checkbox"
                        checked={showConsumptionDocuments}
                        onChange={(e) => setShowConsumptionDocuments(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                      />
                      Verzehrkosten ({consumptionDocumentCount})
                    </label>
                    <label className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${showAccountStatements ? 'border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-800 dark:bg-primary-900/25 dark:text-primary-300' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800'}`}>
                      <input
                        type="checkbox"
                        checked={showAccountStatements}
                        onChange={(e) => setShowAccountStatements(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                      />
                      Kontoauszüge ({accountStatementCount})
                    </label>

                    {/* Zeitraum */}
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                        className="min-h-[40px] px-2 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500"
                        title="Von" aria-label="Upload-Datum von"
                      />
                      <span className="text-gray-400 text-sm">–</span>
                      <input
                        type="date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                        className="min-h-[40px] px-2 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500"
                        title="Bis" aria-label="Upload-Datum bis"
                      />
                    </div>

                    {/* Uploader */}
                    {uniqueCreators.length > 0 && (
                      <select
                        aria-label="Uploader filtern"
                        value={filterCreator}
                        onChange={(e) => setFilterCreator(e.target.value)}
                        className="min-h-[40px] px-2 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500"
                      >
                        <option value="all">Alle Uploader</option>
                        {uniqueCreators.map(creator => (
                          <option key={creator} value={creator || ''}>{creator} ({getCreatorCount(creator || '')})</option>
                        ))}
                      </select>
                    )}

                    {/* Treffer + Reset rechts */}
                    <div className="flex items-center gap-2 ml-auto">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {sortedDocs.length} Treffer
                      </span>
                      {hasActiveFilters && (
                        <button
                          onClick={clearAllFilters}
                          className="px-2.5 py-2 text-sm font-medium text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors"
                        >
                          Zurücksetzen
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!isMergeMode && renderUploadTile()}
              {loadError && (
                <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-error-200 bg-error-50 p-4 text-sm text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300">
                  <span className="flex-1">{loadError}</span>
                  <button onClick={() => fetchDocuments()} disabled={isLoadingDocuments} className="min-h-[40px] rounded-lg border border-current px-3 font-semibold disabled:opacity-50">Erneut laden</button>
                </div>
              )}
              {pdfError && (
                <div role="status" className={`mb-4 rounded-xl border p-3 text-sm ${pdfError.includes('❌') || pdfError.includes('😓') ? 'border-error-200 bg-error-50 text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300' : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'}`}>{pdfError}</div>
              )}
              {isLoadingDocuments && <p role="status" className="mb-4 text-sm text-gray-500 dark:text-gray-400">Dokumente werden geladen …</p>}
              {/* ═══════════════════ CONTENT ═══════════════════ */}
              {documents.length === 0 && !isLoadingDocuments && !loadError ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 sm:p-12 border border-gray-200 dark:border-gray-700">
                  <div className="max-w-xl mx-auto text-center">
                    <div className="w-20 h-20 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-6">
                      <DocumentTextIcon className="h-10 w-10 text-gray-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Keine Dokumente</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Lade dein erstes Dokument hoch um zu starten.</p>
                    <button onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors text-sm font-medium">
                      <CloudArrowUpIcon className="h-5 w-5" /> Dokumente hochladen
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Active Filters (nur Tablet: auf lg zeigt die Filterleiste den Status bereits) */}
                  <div className="hidden sm:block lg:hidden">
                    {renderActiveFilterChips()}
                  </div>

                  <div ref={resultsStartRef} className="scroll-mt-4">
                    {!isLoadingDocuments && !loadError && <DocumentPagination
                      page={visiblePage}
                      pageSize={documentsPerPage}
                      total={sortedDocs.length}
                      onPageChange={changePage}
                      onPageSizeChange={setDocumentsPerPage}
                      navigationLabel="Dokumentseiten oben"
                      compact
                    />}
                  </div>

                  {/* Dokumentplätze bleiben ausschließlich Dokumenten vorbehalten. */}
                  <div className="mb-6 grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                    {paginatedDocs.map((doc) => renderDocumentCard(doc))}
                  </div>

                  {/* No Results */}
                  {documents.length > 0 && sortedDocs.length === 0 && (
                    <div className="text-center py-12">
                      <MagnifyingGlassIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Keine Dokumente gefunden</p>
                      {(hasActiveFilters || statusFilter !== 'alle') && (
                        <button
                          onClick={() => { clearAllFilters(); setStatusFilter('alle'); }}
                          className="mt-2 text-xs font-medium text-primary-600 dark:text-primary-400"
                        >
                          Filter zurücksetzen
                        </button>
                      )}
                      {!hasActiveFilters && statusFilter === 'alle' && (consumptionDocumentCount > 0 || accountStatementCount > 0) && (
                        <button
                          onClick={() => {
                            if (consumptionDocumentCount > 0) setShowConsumptionDocuments(true);
                            if (accountStatementCount > 0) setShowAccountStatements(true);
                          }}
                          className="mt-2 text-xs font-medium text-primary-600 dark:text-primary-400"
                        >
                          Ausgeblendete Spezialdokumente anzeigen
                        </button>
                      )}
                    </div>
                  )}

                  {!isLoadingDocuments && !loadError && <DocumentPagination
                    page={visiblePage}
                    pageSize={documentsPerPage}
                    total={sortedDocs.length}
                    onPageChange={changePage}
                    onPageSizeChange={setDocumentsPerPage}
                    navigationLabel="Dokumentseiten unten"
                    showPageSize={false}
                  />}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════ MOBILE FILTER SHEET ═══════════════════ */}
      <ModalShell
        open={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        variant="choice"
        size="sm"
        icon={FunnelIcon}
        title="Filter"
        subtitle={`${additionalFilteredDocs.length} Ergebnisse`}
        footer={<ModalActions primary={{ label: 'Anwenden', onClick: () => setShowMobileFilters(false) }} />}
      >
        {renderFilterContent()}
      </ModalShell>

      {/* ═══════════════════ MODALS ═══════════════════ */}

      {/* Document Detail Modal */}
      {selectedDocId && (
        <DocumentDetailModal
          documentId={selectedDocId}
          onClose={() => setSelectedDocId(null)}
          onDelete={(id) => { handleDeleteDocument(id); setSelectedDocId(null); }}
          onAIScan={(id) => {
            const d = documents.find(x => x.id === id);
            if (d) setAiModalDoc(d);
          }}
          onDownload={downloadDocument}
          // Liste nachladen, damit Belegnummer, Name und Betrag in der Karte
          // sofort stimmen und nicht erst nach einem Seitenwechsel.
          onDocumentUpdated={() => { setThumbnailVersion(Date.now()); fetchDocuments(); }}
          onOpenAi={(doc) => setAiModalDoc(doc)}
          refreshToken={detailRefresh}
        />
      )}

      {/* Quick Booking Modal */}
      <QuickBookingModal
        isOpen={!!quickBookingDoc}
        onClose={() => setQuickBookingDoc(null)}
        onSuccess={() => { fetchDocuments(); setQuickBookingDoc(null); }}
        document={quickBookingDoc}
      />

      {/* Delete Confirmation */}
      <ModalShell
        open={showDeleteConfirm}
        onClose={() => { if (!isDeleting) { setShowDeleteConfirm(false); setDocsToDelete([]); } }}
        variant="confirm"
        size="sm"
        accent="danger"
        icon={ExclamationTriangleIcon}
        title={`${docsToDelete.length} Dokument(e) löschen?`}
        closeOnBackdrop={false}
        footer={
          <ModalActions
            danger
            secondary={{ label: 'Abbrechen', disabled: isDeleting, onClick: () => { setShowDeleteConfirm(false); setDocsToDelete([]); } }}
            primary={{ label: isDeleting ? 'Wird gelöscht …' : 'Löschen', loading: isDeleting, disabled: isDeleting, onClick: confirmDelete }}
          />
        }
      >
        <p className="text-gray-500 dark:text-gray-400 text-center text-sm">Diese Aktion kann nicht rückgängig gemacht werden.</p>
      </ModalShell>

      {/* Upload Progress */}
      {isUploading && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 animate-ping"><CloudArrowUpIcon className="h-16 w-16 text-primary-400 opacity-40" /></div>
                <CloudArrowUpIcon className="h-16 w-16 text-primary-600 relative animate-bounce" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Hochladen...</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {uploadProgress.total > 1 ? `${uploadProgress.total} Dokumente` : '1 Dokument'}
              </p>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-primary-500 rounded-full animate-pulse" style={{ width: '100%' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {/* KI-Ergebnisse prüfen und übernehmen (Design-Entwurf) */}
      <DocumentAiModal
        open={aiModalDoc !== null}
        // Auch beim bloßen Schließen nachladen: die Analyse selbst speichert
        // bereits Betrag, Datum und Detailfelder am Dokument.
        onClose={() => { setAiModalDoc(null); fetchDocuments(); setDetailRefresh(n => n + 1); }}
        doc={aiModalDoc}
        onSaved={() => { fetchDocuments(); setThumbnailVersion(Date.now()); setDetailRefresh(n => n + 1); }}
        // Drehung: nur das betroffene Thumbnail und das Detail-Modal nachladen.
        // Kein fetchDocuments hier - die Liste ändert sich dadurch nicht.
        onDocumentChanged={(docId) => {
          setDocThumbVersion(m => ({ ...m, [docId]: Date.now() }));
          setDetailRefresh(n => n + 1);
        }}
        onBook={(d) => setQuickBookingDoc(d)}
      />

      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant || 'single'}
        showCloseButton={true}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => { if (confirmModal.onConfirm) confirmModal.onConfirm(); setConfirmModal({ ...confirmModal, isOpen: false }); }
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: { text: 'Abbrechen', onPress: () => setConfirmModal({ ...confirmModal, isOpen: false }) },
          confirm: { text: 'Bestätigen', onPress: () => { if (confirmModal.onConfirm) confirmModal.onConfirm(); setConfirmModal({ ...confirmModal, isOpen: false }); } }
        } : undefined}
      />

      {/* Document Access Modal */}
      <DocumentAccessModal isOpen={showAccessModal} onClose={() => setShowAccessModal(false)} />
    </div>
  );
};

export default DocumentManagement;
